import React, { useState, useContext, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { ReactComponent as Plus } from '../../assets/svg/plus.svg';
import { ReactComponent as Edit } from '../../assets/svg/edit.svg'; 
import { JobApplicationStageEmail } from "../../models/JobApplicationStageEmail";
import { Selector } from "../Shared/Selector";
import EmailHtmlEditor from "../Shared/Inputs/EmailHtmlEditor";
import { Button } from "../Shared/Buttons/Button";
import { TextInput } from "../Shared/Inputs/TextInput";
import { CandidateToEmailTemplate } from "../../helpers/LayoutTexts";
import { useAlert } from "../../hooks/useAlert";
import { PopUpWindow } from '../Shared/PopUpWindow';
import { ReplyMailLoader } from "../../components/Loaders/ReplyMailLoader";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedRecruiter } from "../../models/LoggedRecruiter";
import { SelectorModel } from "../../models/SelectorModel";
import { SummaryOfCompletionModel } from "../../models/SummaryOfCompletionModel";
import { EmailSubjectValidation, VacancyEditResponse } from "../../models/VacancyPostingModel";

interface Props {  
    styles: any;
    jobId?: number;
    onUpdateSummary: (model?: Partial<SummaryOfCompletionModel>) => void;
    onTabChange: (tab: string) => void;
    onLoad: () => Promise<VacancyEditResponse>
    onSave: (model: VacancyEditResponse) => void;
} 

export interface ReplyMailEditHandle {
    saveTab: () => Promise<{ data: VacancyEditResponse } | null>;
} 

const initialValidation: EmailSubjectValidation = {
    subject: {}
}

export const ReplyMailEdit = forwardRef<ReplyMailEditHandle, Props>((props, ref) => {
    const jobId = props.jobId;
    const styles = props.styles;
    const authContext = useContext(AuthContext);
    const recruiter: LoggedRecruiter = authContext.getLoggedRecruiter();    
    const [applicationStatuses, setApplicationStatuses] = useState<SelectorModel[]>([]);
    const [emailList, setEmailList] = useState<JobApplicationStageEmail[]>([]); 
    const [updatedEmailList, setUpdatedEmailList] = useState<JobApplicationStageEmail[]>([]);   
    const [editEnable, setEditEnable] = useState<boolean>(false);
    const [editEmailIndex, setEditEmailIndex] = useState<number>(0);
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);    
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [validation, setValidation] = useState<EmailSubjectValidation>(initialValidation);    
    const { Alert, openAlert } = useAlert();
    const topRef = useRef<HTMLDivElement>(null);
    const lastMailRef = useRef<HTMLDivElement | null>(null);
    const [deletedEmailIds, setDeletedEmailIds] = useState<number[]>([]);  

    const initailEmailModel: JobApplicationStageEmail = {
        jobApplicationStageEmailId: 0,
        jobId: jobId,
        applicationStatusId: 10,        
        triggerTime: 0,
        subject: "",        
        template: CandidateToEmailTemplate
    };
    const [emailModel, setEmailModel] = useState<JobApplicationStageEmail>(initailEmailModel);

    useEffect(()=> {
        getReplyMailDetails();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [])

    const getReplyMailDetails = async () => {
        const emailDetails = await props.onLoad();
        setApplicationStatuses(emailDetails?.emailApplicationStatues ?? []);
        setEmailList(emailDetails.emailList ?? []);
        setUpdatedEmailList(emailDetails.updatedEmailList ?? []);
        setDeletedEmailIds(emailDetails.deletedEmailIds ?? []);
    }
    
    const triggerTimes: SelectorModel[] = [
        { id: 0, value: "Instant" },
        { id: 1, value: "1 Hour" },
        { id: 24, value: "24 Hours" },
        { id: 48, value: "2 Days" },
        { id: 120, value: "5 Days" },
        { id: 168, value: "7 Days" },
        { id: 240, value: "10 Days" }
    ];
    
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setEmailModel((values: any) => ({ ...values, [name]: value }));

        if(validation[name] && validation[name]!.invalid) {
            validateInput(name, value);
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
                
        validateInput(name, value);        
    }

    const handleSelectItems = (selector: string, selectedItem: SelectorModel) => {
        if(selector === "applicationStatus") {
            setEmailModel((values: any) => ({ ...values, applicationStatusId: selectedItem.id }));       
        }
        if(selector === "triggerTime") {            
            setEmailModel((values: any) => ({ ...values, ["triggerTime"]: selectedItem.id}));     
        }
    }

    const validateInput = (name: string, value: string) => { 
        if(name === "subject") {            
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Subject is required"}}));
            } 
            else{
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
    }

    const validateStageEmail = (): number => {
        let emailValidation: EmailSubjectValidation = {
            subject: {valid: true}
        }

        if(emailModel.subject === "" || emailModel.subject === undefined) { 
            emailValidation.subject = {valid: false, invalid: true, invalidMessage: "Subject is required"};
        }
        setValidation(emailValidation);       
        const validInputCount = Object.keys(emailValidation).filter(key => emailValidation[key].valid).length;
        return validInputCount; 
    }

    const handlAddEmailClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setEmailModel(initailEmailModel);
        setEditEnable(false);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });   
    }

    const handleEditEmailClick = (email: JobApplicationStageEmail, index: number) => {        
        setEmailModel({
            jobApplicationStageEmailId: email.jobApplicationStageEmailId,
            jobId: email.jobId,                        
            applicationStatusId: email.applicationStatusId,
            triggerTime: email.triggerTime,
            subject: email.subject,            
            template: email.template
        })
        validateInput("subject", email.subject);
        setEditEnable(true);
        setEditEmailIndex(index);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });    
    }
    
    const handleDeleteEmailClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setOpenDeleteDialog(true);
    }

    const handleDeleteCancel = () => {
        setOpenDeleteDialog(false);    
    }

    const handleDeleteConfirm = () => {
        deleteStageEmail();
        setOpenDeleteDialog(false);
    }

    async function addStageEmail(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = validateStageEmail();
        if (validInputCount == 1) {
            const newId = Math.floor(Math.random() * -1000);
            const newEmail = {
                ...emailModel,
                jobApplicationStageEmailId: newId,
                jobId: jobId,
            };

            setEmailList(prev => [...prev, newEmail]);
            setUpdatedEmailList(prev => [...prev, newEmail]); 

            setEmailModel(initailEmailModel);
            setValidation(initialValidation);
            setTimeout(() => {
                lastMailRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 0);
            openAlert("success", "Email added successfully.");
        } else {
            openAlert("error", "Some mandatory fields are invalid.");
        }
    }

    async function updateStageEmail(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = validateStageEmail();
        if(validInputCount == 1) {    
            setEmailList(prev => 
                prev.map((item, index) => 
                    index === editEmailIndex
                        ? { ...item, ...emailModel }
                        : item
                )
            );

            const existsInEmailList = updatedEmailList.some(q => q.jobApplicationStageEmailId === emailModel.jobApplicationStageEmailId);
            if(existsInEmailList) {
                setUpdatedEmailList(prev => prev.map(m => 
                    m.jobApplicationStageEmailId === emailModel.jobApplicationStageEmailId ? emailModel : m
                ));
            } 
            else {
                setUpdatedEmailList(prev => [...prev, emailModel]);
            }
            setIsLoading(false);
            setEmailModel(initailEmailModel);
            setValidation(initialValidation);
            setEditEnable(false);
            openAlert("success", "Email updated successfully.");  
        }
        else {           
            openAlert("error", "Some mandatory fields are invalid.");
        }
    }

    async function deleteStageEmail(event?: React.FormEvent<HTMLFormElement>) {
        event?.preventDefault();
        const idToDelete = emailModel.jobApplicationStageEmailId;
        setDeletedEmailIds(prev => [...prev, idToDelete]);

        setUpdatedEmailList(prev => prev.filter(q => q.jobApplicationStageEmailId !== idToDelete));
        const updatedList = emailList.filter(mail => mail.jobApplicationStageEmailId !== emailModel.jobApplicationStageEmailId);
        setEmailList(updatedList);
        setEmailModel(initailEmailModel);
        setEditEnable(false);
        openAlert("success", "Email deletd successfully.");    
    }

    const handleDiscardClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setEmailModel(initailEmailModel);
        setEditEnable(false);
    }

    const getApplicationStatus = (statusId: number): string | undefined => { 
        if(applicationStatuses) {   
            const applicationStatus = applicationStatuses.find((element) => element.id === statusId);
            return applicationStatus?.value;
        }
        return undefined;
    }

    const getTriggerTimeText = (triggerTime: number): string | undefined => {        
        const item = triggerTimes.find((element) => element.id === triggerTime);
        return item?.value;
    }

    const saveTab = async (): Promise<{ data: VacancyEditResponse } | null> => {
        const updatedModel: VacancyEditResponse = {
            jobId: jobId!,
            recruiterId: recruiter.recruiterId,
            deletedEmailIds: deletedEmailIds,
            updatedEmailList: updatedEmailList,
            emailList: emailList
        };

        props.onUpdateSummary({
            optional: (emailList.length > 0),
            submission: true
        });
        props.onSave(updatedModel);

        return { data: updatedModel };
    };

    useImperativeHandle(ref, () => ({ saveTab }));
    
    return(
        <>
            {isLoading ? <ReplyMailLoader/> : <div style={styles.mainWrapper}>
                <div style={styles.subTittleWrapper} ref={topRef}>
                    <div style={styles.subTittle}>{"Reply Mail (Optional)"}</div>
                </div>
                <div style={styles.emailWrapper}>
                    <div style={styles.emailHeader}>                    
                        {editEnable ? <span>{`${getApplicationStatus(emailModel.applicationStatusId)} (Edit)`}</span> : <span>{"New Mail"}</span>}
                    </div>
                    <div style={styles.groupWrapper}>
                        <div style={styles.gropuInputWrapper}>
                            <Selector
                                label={"Application Stage"}
                                placeHolder={"Select One"}
                                items={applicationStatuses}
                                name={"applicationStatus"}
                                value={getApplicationStatus(emailModel.applicationStatusId)}
                                onSelectItem={handleSelectItems}
                                styles={styles.selector}
                            />
                        </div>
                        <div style={styles.gropuInputWrapper}>
                            <Selector
                                label={"Send After"}
                                placeHolder={"Select Option"}
                                items={triggerTimes}
                                name={"triggerTime"}
                                value={getTriggerTimeText(emailModel.triggerTime)}
                                onSelectItem={handleSelectItems}
                                styles={styles.selector}
                            />
                        </div>
                    </div>
                    <div style={styles.inputWrapper}>
                        <label style={styles.label}>{"Subject *"}</label>
                        <TextInput
                            inputType={"text"}
                            name={"subject"}
                            value={emailModel.subject}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            styles={styles.textInput}
                            validation={validation.subject}     
                        />
                    </div>
                    <div style={styles.emailTitle}>{"Email"}</div>
                    <div>
                        <EmailHtmlEditor  
                            styles={styles.coverLetterEditor}
                            value={emailModel.template} 
                            onChange={(content: string) => setEmailModel((values: any) => ({ ...values, ["template"]: content }))} 
                        />
                    </div>
                    {editEnable ? <div style={styles.buttonWrapper}>
                        <div style={styles.buttonWrapper.group}>
                            <div style={styles.buttonWrapper.topRow}>
                                <Button style={styles.buttonWrapper.update} onClick={updateStageEmail}>{"UPDATE"}</Button>
                            </div>
                            <div style={styles.buttonWrapper.bottomRow}>
                                <Button style={styles.buttonWrapper.saveDraft} onClick={handleDiscardClick}>{"DISCARD"}</Button>
                                <Button style={styles.buttonWrapper.delete} onClick={handleDeleteEmailClick}>{"DELETE"}</Button>
                            </div>
                        </div>                        
                    </div>
                    : <div style={styles.buttonWrapper}>
                        <Button style={styles.buttonWrapper.submit} onClick={addStageEmail}>{"ADD EMAIL"}</Button>
                        <Button style={styles.buttonWrapper.saveDraft} onClick={handleDiscardClick}>{"DISCARD"}</Button>
                    </div>}
                </div>
                {emailList.map((email, index) => {
                    const isLast = index === emailList.length - 3;
                    return (
                        <div 
                            key={email.applicationStatusId} 
                            style={styles.emailList}
                            ref={isLast ? lastMailRef : null}
                        >                        
                            {getApplicationStatus(email.applicationStatusId)}
                            <Edit style={styles.editIcon} onClick={() => handleEditEmailClick(email, index)}/>
                        </div>
                    );
                })}   
                <div style={styles.singleButton}>
                    <Button style={styles.singleAddNewQuestionButton} onClick={handlAddEmailClick}>{"ADD NEW EMAIL"}<Plus style={styles.plusIcon}/></Button>
                </div>
            </div>}
            <PopUpWindow styles={styles.deleteWarningDialog} open={openDeleteDialog} toggle={()=> setOpenDeleteDialog(!openDeleteDialog)} closeCircle={true}>
                <h1 style={styles.deleteDialogTitle}>{"Are you sure?"}</h1>
                <p style={styles.deleteDialogMessage}>{"This email template will be deleted permanently."}</p>
                <div className="center-content" style={styles.deleteButtons}>
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.red}} onClick={handleDeleteConfirm}>{"YES"}</Button>
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.blue}} onClick={handleDeleteCancel}>{"NO"}</Button> 
                </div>
            </PopUpWindow>
            <Alert />
        </>
    )
})